import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  DEFAULT_SUBMITTED_VALUE_LABEL,
  ReviewEdge,
  ReviewStatus,
  ReviewValue,
  UI2ReviewFieldProps,
  UI2_REVIEW_FIELD_DEFAULT_PROPS,
  isReviewValueEmpty,
} from 'shared-libs/components/ui2/fields/ui2-review-field/ui2-review-field-props'
import { translateString } from 'shared-libs/helpers/utils'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2ActionJustificationPanel } from '../field-ui/action-justification-panel/action-justification-panel'

import './_ui2-review-field.scss'

interface IDecisionSpec {
  status: ReviewStatus
  label: string
  icon: any
}

interface IUI2ReviewFieldWebExtras extends Omit<IBaseProps, 'children'> {
  onChange?: (value: any) => void
  errors?: any[]
  frames?: FramesManager
  translationTable?: any
  isHorizontalLayout?: boolean
}

/**
 * Wraps a driver-entered form field so a facility user can Approve / Reject /
 * Override the submitted value. The wrapped field is passed as a `fieldTemplate`
 * uiSchema fragment — the wrapper materialises it via the entity renderer and
 * controls read-only / editable state at creation time.
 *
 * @uiComponent
 */
export interface IUI2ReviewFieldProps extends UI2ReviewFieldProps, IUI2ReviewFieldWebExtras {}

interface IState {
  isOtherModeActive: boolean
}

export class UI2ReviewField extends React.Component<IUI2ReviewFieldProps, IState> {
  public static defaultProps: Partial<IUI2ReviewFieldProps> = {
    ...UI2_REVIEW_FIELD_DEFAULT_PROPS,
  }

  public static isolatesChildValueScope = true
  public static isValueEmptyForRequired = isReviewValueEmpty

  constructor(props: IUI2ReviewFieldProps) {
    super(props)
    this.state = {
      isOtherModeActive: UI2ReviewField.computeInitialOtherMode(props),
    }
  }

  private static computeInitialOtherMode(props: IUI2ReviewFieldProps): boolean {
    const status = props.value?.status
    const reason = props.value?.reason
    if (!reason || (status !== 'rejected' && status !== 'overridden')) return false
    const list = status === 'overridden' ? props.overrideReasons : props.rejectionReasons
    if (_.isEmpty(list)) return false
    return !(list ?? []).some((r) => r === reason)
  }

  public componentDidMount(): void {
    this.restoreOriginalWhenRejected()
    this.assertValuePathShape()
  }

  public componentDidUpdate(): void {
    this.restoreOriginalWhenRejected()
  }

  private restoreOriginalWhenRejected(): void {
    const { value, frames, fieldTemplate } = this.props
    if (value?.status !== 'rejected') return
    const originalValue = value?.originalValue
    if (originalValue === undefined || originalValue === null || originalValue === '') return
    const childPath: string | undefined = fieldTemplate?.value
    if (!childPath) return
    const currentChildValue = frames?.getValue?.(childPath)
    if (currentChildValue === originalValue) return
    if (currentChildValue !== null && currentChildValue !== undefined && currentChildValue !== '') return
    frames?.getAction?.('setValue')?.(childPath, originalValue)
  }

  private assertValuePathShape(): void {
    const { frames } = this.props
    const dataSchema = frames?.getContext?.('dataSchema')
    const valuePath = frames?.getContext?.('valuePath')
    const pathLabel = _.isArray(valuePath) ? valuePath.join('.') : '<unknown>'
    if (!dataSchema || typeof dataSchema !== 'object') return
    if (dataSchema.type && dataSchema.type !== 'object') {
      // eslint-disable-next-line no-console
      console.error(
        `[ui2:reviewField] value path "${pathLabel}" must resolve to an object schema ` +
          `(shape: {status, reason, originalValue, reviewedBy}); found type="${dataSchema.type}".`
      )
      return
    }
    const statusEnum: unknown = dataSchema.properties?.status?.enum
    const expected = ['accepted', 'rejected', 'overridden']
    const enumMatches =
      _.isArray(statusEnum) && expected.every((s) => (statusEnum as string[]).includes(s))
    if (!enumMatches) {
      // eslint-disable-next-line no-console
      console.error(
        `[ui2:reviewField] value path "${pathLabel}" is not an acceptRejectOverrideField ` +
          `(expected properties.status.enum to include ${JSON.stringify(expected)}). ` +
          `Point the field at a property that $refs "acceptRejectOverrideField" in entity.json.`
      )
    }
  }

  private currentReviewer(): ReviewEdge | undefined {
    try {
      const user: any = apis.getSettings?.()?.getUser?.()
      if (!user) return undefined
      const entityId: string | undefined = user.uniqueId ?? user.get?.('uniqueId')
      if (!entityId) return undefined
      const displayName: string | undefined = user.displayName ?? user.get?.('displayName')
      const edge: ReviewEdge = { entityId, entityType: '/1.0/entities/metadata/user.json' }
      if (displayName) edge.displayName = displayName
      return edge
    } catch {
      return undefined
    }
  }

  private static resolveToggles(props: IUI2ReviewFieldProps) {
    const allowOverride = !!props.allowOverride
    let allowRejection = props.allowRejection !== false
    if (!allowOverride && !allowRejection) allowRejection = true
    return { allowOverride, allowRejection }
  }

  private mergedErrors(): any[] | undefined {
    const { errors } = this.props
    const errorsMap = (this.props as any).errorsMap
    const subPathErrors: any[] = []
    if (errorsMap && typeof errorsMap === 'object') {
      for (const key of Object.keys(errorsMap)) {
        if (key === '_errors') continue
        const child = errorsMap[key]
        if (child && _.isArray(child._errors)) subPathErrors.push(...child._errors)
      }
    }
    if (_.isEmpty(subPathErrors)) return errors
    return [...(errors || []), ...subPathErrors]
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  public render(): React.ReactNode {
    const { className } = this.props
    const merged = this.mergedErrors()
    return (
      <UI2FieldShell
        {...this.props}
        errors={merged}
        isValueEmpty={isReviewValueEmpty}
        // The read-only body shows the driver's value, the status badge and
        // the reviewer even with no decision recorded — an empty review is
        // not an empty field.
        suppressDefaultEmptyState={true}
        renderReadOnly={(_v, { editNode }) => this.renderReadOnlyBody(editNode)}
        renderEditable={(_, extras) => this.renderEditableBody(extras.cancelNode)}
        className={classNames('ui2-reviewField', className)}
      >
        {null}
      </UI2FieldShell>
    )
  }

  private renderEditableBody(cancelNode?: React.ReactNode): React.ReactNode {
    const { value, isDisabled } = this.props
    const status = value?.status
    const { allowOverride, allowRejection } = UI2ReviewField.resolveToggles(this.props)
    const merged = this.mergedErrors()
    const hasError = !_.isEmpty(merged)
    const showButtonErrorTint = hasError && !status
    const isNoDecision = !status
    return (
      <div
        className={classNames('ui2-reviewField__body', {
          'ui2-reviewField__body--noDecision': isNoDecision,
        })}
      >
        {this.renderSubmittedValueCard(status)}
        <div className='ui2-reviewField__decision'>
          {this.buildDecisionSpecs(allowRejection, allowOverride).map((spec) =>
            this.renderDecisionButton(spec, status, !!isDisabled)
          )}
          {cancelNode && (
            <div className='ui2-reviewField__cancelSlot'>{cancelNode}</div>
          )}
        </div>
      </div>
    )
  }

  private renderReadOnlyBody(editNode: React.ReactNode): React.ReactNode {
    const { value } = this.props
    const status = value?.status
    const hasDriverValue = this.hasDriverValueInChild()
    const badge = this.buildBadgeSpec(status, hasDriverValue)
    return (
      <div className='ui2-reviewField__readOnly'>
        <div className='ui2-reviewField__readOnlyRow'>
          <div className='ui2-reviewField__readOnlyValue'>
            {this.renderChild(false)}
          </div>
          {badge && (
            <StatusBadge
              className='ui2-reviewField__statusBadge'
              color={badge.color}
              text={badge.label}
              frames={this.props.frames}
            />
          )}
          {editNode}
        </div>
        {status && value?.reason && (
          <div className='ui2-reviewField__readOnlyReason'>Reason: {value.reason}</div>
        )}
        {status && value?.reviewedBy && (
          <div className='ui2-reviewField__readOnlyReviewer'>
            Reviewed by: {value.reviewedBy.displayName ?? value.reviewedBy.entityId}
          </div>
        )}
      </div>
    )
  }

  // ─── Submitted-value card ────────────────────────────────────────────────

  private renderSubmittedValueCard(status: ReviewStatus | undefined): React.ReactNode {
    const { submittedValueLabel = "", translationTable, frames } = this.props
    const table = Object.assign({}, translationTable, frames?.getContext?.('translationTable'))
    const captionText = translateString(submittedValueLabel, table) ?? DEFAULT_SUBMITTED_VALUE_LABEL
    const showReason = status === 'rejected' || status === 'overridden'
    const cardStateClass = status
      ? `ui2-reviewField__card--${status}`
      : 'ui2-reviewField__card--pending'
    return (
      <div className='ui2-reviewField__cardWrap'>
        <div className='ui2-reviewField__caption'>{captionText}</div>
        <div className={classNames('ui2-reviewField__card', cardStateClass)}>
          <div className='ui2-reviewField__cardValue'>
            {this.renderChild(status === 'overridden')}
          </div>
          {showReason && (
            <div key={status} className='ui2-reviewField__cardReason'>
              {this.renderReason(status as 'rejected' | 'overridden')}
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderChild(isOverriding: boolean): React.ReactNode {
    const { fieldTemplate, frames } = this.props
    if (!fieldTemplate) {
      return (
        <div className='ui2-reviewField__warning'>
          ui2:reviewField requires a `fieldTemplate` uiSchema fragment.
        </div>
      )
    }
    const renderer = frames?.getContext?.('renderer')
    if (!renderer) return null
    const childUiSchema = {
      ...fieldTemplate,
      isReadOnly: !isOverriding,
      showPrefilledIndicator: false,
      isHorizontalLayout: false,
      label: null,
      autoFocus: isOverriding,
    }
    const uiSchemaPath = (frames?.getContext('uiSchemaPath') ?? []).concat('fieldTemplate')
    const newFrames = renderer.createChildFrame(frames, { uiSchema: childUiSchema, uiSchemaPath })
    return renderer.createElementFromFrame(newFrames)
  }

  // ─── Decision buttons ────────────────────────────────────────────────────

  private buildDecisionSpecs(allowRejection: boolean, allowOverride: boolean): IDecisionSpec[] {
    const specs: IDecisionSpec[] = [{ status: 'accepted', label: 'Approve', icon: <UI2Icon name={UI2IconNames.CHECK} /> }]
    if (allowRejection) specs.push({ status: 'rejected', label: 'Reject', icon: <UI2Icon name={UI2IconNames.X} /> })
    if (allowOverride) specs.push({ status: 'overridden', label: 'Override', icon: <UI2Icon name={UI2IconNames.PENCIL} /> })
    return specs
  }

  private renderDecisionButton(
    spec: IDecisionSpec,
    status: ReviewStatus | undefined,
    isDisabled: boolean
  ): React.ReactNode {
    const isActive = status === spec.status
    const { frames } = this.props
    const handleClick = () => this.handleDecisionChange(isActive ? undefined : spec.status)
    return (
      <div
        key={spec.status}
        className={classNames(
          'ui2-reviewField__decisionAction',
          `ui2-reviewField__decisionAction--${spec.status}`,
          { 'ui2-reviewField__decisionAction--active': isActive },
          { 'ui2-reviewField__decisionAction--disabled': isDisabled }
        )}
      >
        <Button
          className={classNames(
            'ui2-reviewField__decisionButton',
            `ui2-reviewField__decisionButton--${spec.status}`,
            { 'ui2-reviewField__decisionButton--active': isActive }
          )}
          buttonText={spec.label}
          icon={spec.icon}
          isActive={isActive}
          isDisabled={isDisabled}
          onClick={handleClick}
          frames={frames}
        />
        <span
          className='ui2-reviewField__decisionLabel'
          onClick={isDisabled ? undefined : handleClick}
        >
          {spec.label}
        </span>
      </div>
    )
  }

  // ─── Decision write-paths ────────────────────────────────────────────────

  private handleDecisionChange = (nextStatus: ReviewStatus | undefined): void => {
    const { value, onChange, frames } = this.props
    if (!onChange) return
    const childPath: string | undefined = this.props.fieldTemplate?.value
    const currentChildValue = childPath ? frames?.getValue?.(childPath) : undefined
    const originalValue = value?.originalValue ?? currentChildValue
    const restoreOriginalIfNeeded = () => {
      if (!childPath || originalValue === undefined || currentChildValue === originalValue) return
      frames?.getAction?.('setValue')?.(childPath, originalValue)
    }
    const reviewedBy = this.currentReviewer()
    if (nextStatus === undefined) {
      restoreOriginalIfNeeded()
      onChange({ originalValue })
      this.setState({ isOtherModeActive: false })
      return
    }
    if (nextStatus === 'accepted') {
      restoreOriginalIfNeeded()
      onChange({ status: 'accepted', originalValue, reviewedBy })
      this.setState({ isOtherModeActive: false })
      return
    }
    if (nextStatus === 'rejected') {
      onChange({ status: 'rejected', originalValue, reviewedBy })
      this.setState({ isOtherModeActive: false })
      return
    }
    if (nextStatus === 'overridden') {
      onChange({ status: 'overridden', originalValue, reviewedBy })
      this.setState({ isOtherModeActive: false })
    }
  }

  // ─── Reason panel ────────────────────────────────────────────────────────

  private renderReason(status: 'rejected' | 'overridden'): React.ReactNode {
    const { rejectionReasons, overrideReasons, isDisabled, frames, translationTable, value } = this.props
    const reasons = (status === 'overridden' ? overrideReasons : rejectionReasons) ?? []
    const promptLabel = status === 'overridden' ? 'Override reason' : 'Rejection reason'
    const { isOtherModeActive } = this.state
    return (
      <div className='ui2-reviewField__reason'>
        <UI2ActionJustificationPanel
          reasons={reasons.map((r) => ({ label: r, value: r }))}
          reason={UI2ReviewField.normalizeReason(value?.reason)}
          label={promptLabel}
          placeholder='Please explain…'
          isDisabled={isDisabled}
          isPickerRequired={status === 'rejected'}
          isFreeformRequired={status === 'rejected' || isOtherModeActive}
          frames={frames}
          translationTable={translationTable}
          onReasonChange={this.handleReasonChange}
          onOtherModeChange={this.handleOtherModeChange}
        />
      </div>
    )
  }

  private handleReasonChange = (next: string | undefined): void => {
    this.emitReason(next)
  }

  private handleOtherModeChange = (isOtherModeActive: boolean): void => {
    if (this.state.isOtherModeActive === isOtherModeActive) return
    this.setState({ isOtherModeActive })
  }

  private emitReason(next: string | undefined): void {
    const { value, onChange } = this.props
    if (!onChange || !value?.status) return
    const trimmed = typeof next === 'string' ? next.trim() : ''
    const rest = _.omit(value, 'reason')
    const reviewedBy = this.currentReviewer() ?? value.reviewedBy
    const base = reviewedBy ? { ...rest, reviewedBy } : rest
    if (trimmed) {
      onChange({ ...base, reason: trimmed })
    } else {
      onChange(base as ReviewValue)
    }
  }

  private static normalizeReason(raw: any): string | undefined {
    if (raw == null) return undefined
    if (typeof raw === 'string') return raw
    if (typeof raw === 'object' && 'value' in raw && typeof raw.value === 'string') return raw.value
    return undefined
  }

  // ─── Read-only helpers ───────────────────────────────────────────────────

  private hasDriverValueInChild(): boolean {
    const childPath: string | undefined = this.props.fieldTemplate?.value
    if (!childPath) return false
    const v = this.props.frames?.getValue?.(childPath)
    if (v === null || v === undefined || v === '') return false
    if (_.isArray(v) && v.length === 0) return false
    return true
  }

  private buildBadgeSpec(
    status: ReviewStatus | undefined,
    hasDriverValue: boolean
  ): { label: string; color: string } | undefined {
    if (status === 'accepted') return { label: 'Approved', color: 'green' }
    if (status === 'rejected') return { label: 'Rejected', color: 'red' }
    if (status === 'overridden') return { label: 'Overridden', color: 'amber' }
    if (hasDriverValue) return { label: 'Review Pending', color: 'gray' }
    return undefined
  }
}
